<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="text-left card-header fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="6">
					<v-toolbar-title>{{ $t('add') }} {{ $t('item') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pa-4 pb-0 overflow-y-auto" style="height: calc(100vh - 122px)">
			<v-row dense>
				<v-col cols="12">
					<v-select
						v-model="item.itemModelId"
						:label="$t('item-model')"
						:items="itemCatalogs"
						item-text="name"
						item-value="id"
						dense
						outlined
						hide-details
						:menu-props="{ offsetY: true }" />
				</v-col>
				<v-col cols="12">
					<v-text-field v-model="item.serialNumber" :label="$t('serial-number')" dense outlined hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field v-model="item.assetTag" :label="$t('asset-tag')" dense outlined hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field
						v-model="item.remoteConnectIdString"
						:label="$t('remote-connect-id')"
						dense
						outlined
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-menu
						ref="priceDateMenu"
						v-model="priceDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedPurchaseDate"
								:label="$t('purchase-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker
							v-model="item.purchaseDate"
							no-title
							:max="new Date().toISOString().substring(0, 10)"
							@input="priceDateMenu = false" />
					</v-menu>
				</v-col>
				<v-col cols="12">
					<v-text-field
						v-model="item.purchasePrice"
						:label="$t('price')"
						dense
						outlined
						inputmode="decimal"
						:rules="priceRules"
						@input="onPriceInput"
						@keypress="(e) => /[0-9.]/.test(e.key) || e.preventDefault()"
						hide-details />
				</v-col>
				<v-col cols="12">
					<v-text-field v-model="item.purchaseCurrency" :label="$t('purchase-currency')" dense outlined hide-details />
				</v-col>
				<v-col cols="12">
					<v-menu
						ref="warrantyEndDateMenu"
						v-model="warrantyEndDateMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto">
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="formattedWarrantyEndDate"
								:label="$t('warranty-end-date')"
								prepend-inner-icon="mdi-calendar"
								readonly
								outlined
								dense
								hide-details
								v-bind="attrs"
								v-on="on" />
						</template>
						<v-date-picker v-model="item.warrantyEndDate" no-title @input="warrantyEndDateMenu = false" />
					</v-menu>
				</v-col>
				<v-col cols="12">
					<v-textarea v-model="item.note" :label="$t('notes')" dense outlined hide-details no-resize />
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense>
				<v-col cols="6" order="1">
					<v-btn elevation="0" class="cancel-button-style capitalize-text" @click="closeDrawer">
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6" order="2">
					<v-btn block color="primary" :disabled="!canSave" @click="save">{{ $t('save') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import { formatTimestamp } from '../../utils/utilities';
export default {
	name: 'AddInventoryItem',
	data () {
		return {
			item: {
				itemModelId: null,
				serialNumber: '',
				assetTag: '',
				remoteConnectIdString: '',
				note: '',
				purchaseDate: null,
				warrantyEndDate: null,
				purchasePrice: null,
				purchaseCurrency: 'USD',
			},
			itemTemplate: {
				itemModelId: null,
				serialNumber: '',
				assetTag: '',
				remoteConnectIdString: '',
				note: '',
				purchaseDate: null,
				warrantyEndDate: null,
				purchasePrice: null,
				purchaseCurrency: 'USD',
			},
			priceDateMenu: false,
			warrantyEndDateMenu: false,
		};
	},
	computed: {
		...mapState({
			itemCatalogs: (state) => state.inventoryItemCatalogs.itemCatalogs,
			selectedItemCatalog: (state) => state.inventoryItems.selectedItemCatalog,
		}),
		canSave () {
			const validPrice = /^\d+(?:\.\d{1,2})?$/.test(String(this.item.purchasePrice));
			return this.item.purchaseDate && validPrice && this.item.purchaseCurrency && this.item.itemModelId;
		},
		formattedPurchaseDate () {
			return this.item.purchaseDate ? this.formatTimestamp(this.item.purchaseDate) : '';
		},
		formattedWarrantyEndDate () {
			return this.item.warrantyEndDate ? this.formatTimestamp(this.item.warrantyEndDate) : '';
		},
		priceRules () {
			return [
				(v) => (v !== null && v !== undefined && String(v).length > 0) || this.$t('required-field'),
				(v) => /^\d+(?:\.\d{1,2})?$/.test(String(v)) || this.$t('invalid-currency-format') || 'Invalid currency',
			];
		},
	},
	async mounted () {
		this.item = { ...this.itemTemplate };
		if (this.selectedItemCatalog) {
			this.item.itemModelId = this.selectedItemCatalog.id;
		}
		if (!this.itemCatalogs?.length) {
			await this.getItemCatalogs();
		}
	},
	methods: {
		...mapActions({
			getItemCatalogs: 'inventoryItemCatalogs/getItemCatalogs',
			getInventoryItems: 'inventoryItems/getInventoryItems',
			addInventoryItem: 'inventoryItems/addInventoryItem',
		}),
		formatTimestamp,
		onPriceInput (val) {
			if (val === null || val === undefined) {
				this.item.purchasePrice = '';
				return;
			}
			let s = String(val).replace(/[^0-9.]/g, '');
			const parts = s.split('.');
			if (parts.length > 1) {
				const intPart = parts.shift();
				const dec = parts.join('');
				s = intPart + (dec ? '.' + dec : '');
			}
			if (s.includes('.')) {
				const [intPart, decPart] = s.split('.');
				s = intPart + '.' + (decPart ? decPart.slice(0, 2) : '');
			}
			this.item.purchasePrice = s;
		},
		closeDrawer () {
			this.item = { ...this.itemTemplate };
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		async save () {
			const formatter = new Intl.DateTimeFormat('en-CA');
			const body = {
				...this.item,
				purchasePrice: Number(this.item.purchasePrice),
				purchaseDate: formatter.format(new Date(this.item.purchaseDate)),
				warrantyEndDate: this.item.warrantyEndDate ? formatter.format(new Date(this.item.warrantyEndDate)) : null,
			};
			await this.addInventoryItem(body);
			await this.getInventoryItems();
			this.closeDrawer();
		},
	},
	watch: {
		selectedItemCatalog (val) {
			if (val) {
				this.item.itemModelId = val.id;
			}
		},
	},
};
</script>
